import { LocalStorage } from 'quasar'
import { Notify } from 'quasar'

export default {
  constructor() {
    this.isElectron = window.electronAPI?.isElectron || false
    this.storagePath = null
    this.memoryUsage = {
      total: 0,
      files: new Map()
    }
    this.maxMemoryUsage = 100 * 1024 * 1024 // 100MB limit
  },

  async initialize() {
    if (this.isElectron) {
      try {
        this.storagePath = await window.electronAPI.getAppPath()
        // Initialize storage through IPC
        const result = await window.electronAPI.initializeStorage(this.storagePath)
        if (!result.success) {
          throw new Error(result.error)
        }

        // Initialize memory usage tracking
        await this.updateMemoryUsage()

        // console.log('Storage initialized in:', this.storagePath)
      } catch (error) {
        console.error('Failed to initialize storage:', error)
        Notify.create({
          type: 'negative',
          message: 'Failed to initialize local storage. Some features may be limited.',
          timeout: 5000
        })
      }
    }
  },

  async updateMemoryUsage() {
    if (this.isElectron) {
      try {
        const result = await window.electronAPI.getStorageStats()
        if (result.success) {
          this.memoryUsage = result.stats
        }
      } catch (error) {
        console.error('Failed to update memory usage:', error)
      }
    }
  },

  async set(key, value) {
    if (this.isElectron) {
      try {
        const result = await window.electronAPI.writeFile(key, JSON.stringify(value))
        if (!result.success) {
          throw new Error(result.error)
        }
        // Also store in LocalStorage for quick access
        LocalStorage.set(key, value)
      } catch (error) {
        console.error('Failed to write to file system:', error)
        // Fallback to LocalStorage
        LocalStorage.set(key, value)
      }
    } else {
      LocalStorage.set(key, value)
    }
  },

  async get(key) {
    if (this.isElectron) {
      try {
        const result = await window.electronAPI.readFile(key)
        if (result.success) {
          return JSON.parse(result.data)
        }
        throw new Error(result.error)
      } catch (error) {
        console.error('Failed to read from file system:', error)
        // Fallback to LocalStorage
        return LocalStorage.getItem(key)
      }
    } else {
      return LocalStorage.getItem(key)
    }
  },

  async remove(key) {
    if (this.isElectron) {
      try {
        const result = await window.electronAPI.deleteFile(key)
        if (!result.success) {
          throw new Error(result.error)
        }
      } catch (error) {
        console.error('Failed to remove file:', error)
      }
    }
    LocalStorage.remove(key)
  },

  async clear() {
    if (this.isElectron) {
      try {
        const result = await window.electronAPI.clearStorage()
        if (!result.success) {
          throw new Error(result.error)
        }
      } catch (error) {
        console.error('Failed to clear storage:', error)
      }
    }
    LocalStorage.clear()
  },

  async getStorageStats() {
    if (this.isElectron) {
      try {
        const result = await window.electronAPI.getStorageStats()
        if (result.success) {
          return result.stats
        }
        throw new Error(result.error)
      } catch (error) {
        console.error('Failed to get storage stats:', error)
        return null
      }
    }
    return null
  },

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }
}
